import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, Dialog, IconButton } from "@mui/material";
import { motion } from "framer-motion";
import { Navigation, Scrollbar } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import ReactImageMagnify from "react-image-magnify";
import "swiper/swiper.min.css";
import ClearIcon from "@mui/icons-material/Clear";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FullscreenIcon from "@mui/icons-material/Fullscreen";

const isVideo = (url) => url?.includes(".mp4") || url?.includes(".mkv") || url?.includes(".mov");

const ListImage = ({ listData, imageParallax }) => {
  const [selectedMedia, setSelectedMedia] = useState();
  const [displayMedia, setDisplayMedia] = useState();
  const [media, setMedia] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [visible, setVisible] = useState(true);
  const transitionTimer = useRef(null);

  // Crossfade when selected image changes
  const switchTo = useCallback((url) => {
    if (url === displayMedia) return;
    setVisible(false);
    clearTimeout(transitionTimer.current);
    transitionTimer.current = setTimeout(() => {
      setDisplayMedia(url);
      setVisible(true);
    }, 160);
  }, [displayMedia]);

  useEffect(() => {
    if (selectedMedia) switchTo(selectedMedia);
  }, [selectedMedia]); // eslint-disable-line

  useEffect(() => {
    if (listData) {
      const mediaCopy = listData.filter((m) => m?.url !== null && m?.url !== undefined);
      setMedia(mediaCopy);
      setSelectedMedia(mediaCopy[0]?.url);
      setDisplayMedia(mediaCopy[0]?.url);
      setVisible(true);
    }
  }, [listData]);

  const navigate = useCallback((dir) => {
    if (!media.length) return;
    const idx = media.findIndex((e) => e.url === selectedMedia);
    const next = dir === "right"
      ? (idx < media.length - 1 ? idx + 1 : 0)
      : (idx > 0 ? idx - 1 : media.length - 1);
    setSelectedMedia(media[next].url);
  }, [media, selectedMedia]);

  const handleKeyDown = (e) => {
    if (e.keyCode === 37) navigate("left");
    else if (e.keyCode === 39) navigate("right");
  };

  if (!listData) return null;

  const currentIndex = media.findIndex((m) => m.url === selectedMedia);

  return (
    <>
      {/* ── Fullscreen lightbox ── */}
      <Dialog
        open={openModal}
        onClose={(e) => { e.preventDefault(); setOpenModal(false); setIsZoomed(false); }}
        onClick={(e) => { e.preventDefault(); setOpenModal(false); setIsZoomed(false); }}
        onKeyDown={handleKeyDown}
        fullScreen
        PaperProps={{
          style: {
            backgroundColor: "rgba(0,0,0,0.92)",
            backdropFilter: "blur(16px)",
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row-reverse", p: 2, gap: 1 }}>
          <IconButton color="primary" onClick={(e) => { e.preventDefault(); setOpenModal(false); setIsZoomed(false); }}>
            <ClearIcon fontSize="large" />
          </IconButton>
          <IconButton color="primary" onClick={(e) => { e.stopPropagation(); setIsZoomed((z) => !z); }}>
            {isZoomed ? <ZoomOutIcon fontSize="large" /> : <ZoomInIcon fontSize="large" />}
          </IconButton>
        </Box>

        {media.length > 1 && (
          <>
            <IconButton
              color="primary"
              onClick={(e) => { e.stopPropagation(); navigate("right"); }}
              sx={{ position: "absolute", top: "50%", right: { sm: "1%", xs: 0 }, transform: "translateY(-50%)" }}
            >
              <EastIcon sx={{ fontSize: { lg: 30, md: 24, xs: 18 } }} />
            </IconButton>
            <IconButton
              color="primary"
              onClick={(e) => { e.stopPropagation(); navigate("left"); }}
              sx={{ position: "absolute", top: "50%", left: { sm: "1%", xs: 0 }, transform: "translateY(-50%)" }}
            >
              <WestIcon sx={{ fontSize: { lg: 30, md: 24, xs: 18 } }} />
            </IconButton>
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "90%", height: "90%", mx: "auto", p: 2 }}>
          {!isZoomed ? (
            <img
              src={selectedMedia}
              alt="listing"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: "8px" }}
            />
          ) : (
            <ReactImageMagnify
              {...{
                isEnlargedImagePortalEnabledForTouch: true,
                shouldHideHintAfterFirstActivation: false,
                hintTextTouch: "Long-Touch to Zoom",
                enlargedImagePosition: "over",
                isHintEnabled: true,
                smallImage: { alt: selectedMedia, isFluidWidth: true, src: selectedMedia },
                largeImage: { src: selectedMedia, width: 2000, height: 2000 },
                enlargedImageContainerStyle: { zIndex: 1500 },
                enlargedImageContainerDimensions: { width: "100%", height: "100%" },
              }}
            />
          )}
        </Box>
      </Dialog>

      {/* ── Main gallery ── */}
      <Box>
        {/* Primary image */}
        <Box
          onClick={(e) => { e.preventDefault(); setOpenModal(true); }}
          sx={{
            position: "relative",
            overflow: "hidden",
            backgroundColor: "#111",
            borderRadius: "12px",
            cursor: "zoom-in",
            mb: 1,
          }}
        >
            {isVideo(displayMedia) ? (
              <video
                src={displayMedia}
                key={displayMedia}
                controls
                style={{
                  width: "100%",
                  display: "block",
                  opacity: visible ? 1 : 0,
                  transition: "opacity 0.25s ease",
                }}
              />
            ) : (
              <img
                src={displayMedia}
                alt="listing"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  opacity: visible ? 1 : 0,
                  transition: "opacity 0.25s ease",
                }}
              />
            )}

          {/* Image counter */}
          {media.length > 1 && (
            <Box
              sx={{
                position: "absolute",
                bottom: 10,
                left: 10,
                zIndex: 1,
                backgroundColor: "rgba(0,0,0,0.6)",
                backdropFilter: "blur(6px)",
                color: "#ccc",
                fontSize: "12px",
                fontWeight: 600,
                px: 1.2,
                py: 0.4,
                borderRadius: "20px",
                border: "1px solid rgba(255,255,255,0.07)",
                userSelect: "none",
              }}
            >
              {currentIndex + 1} / {media.length}
            </Box>
          )}

          {/* Fullscreen icon */}
          <Box
            sx={{
              position: "absolute",
              bottom: 10,
              right: 10,
              zIndex: 1,
              backgroundColor: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(6px)",
              borderRadius: "8px",
              p: 0.5,
              opacity: 0,
              transition: "opacity 0.2s",
              ".gallery-wrapper:hover &": { opacity: 1 },
            }}
          >
            <FullscreenIcon sx={{ color: "#ccc", fontSize: 20, display: "block" }} />
          </Box>

          {/* Hover arrows */}
          {media.length > 1 && (
            <>
              {[{ dir: "left", icon: <WestIcon sx={{ fontSize: 16 }} />, side: { left: 8 } },
                { dir: "right", icon: <EastIcon sx={{ fontSize: 16 }} />, side: { right: 8 } }
              ].map(({ dir, icon, side }) => (
                <Box
                  key={dir}
                  onClick={(e) => { e.stopPropagation(); navigate(dir); }}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    ...side,
                    transform: "translateY(-50%)",
                    zIndex: 1,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    backdropFilter: "blur(4px)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "50%",
                    width: 36, height: 36,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer",
                    color: "#fff",
                    opacity: 0,
                    transition: "opacity 0.2s, background 0.2s",
                    ".gallery-wrapper:hover &": { opacity: 1 },
                    "&:hover": { backgroundColor: "rgba(255,192,1,0.3)", borderColor: "#FFC001", color: "#FFC001" },
                  }}
                >
                  {icon}
                </Box>
              ))}
            </>
          )}
        </Box>

        {/* Thumbnail strip */}
        {media.length > 1 && (
          <Swiper
            style={{ "--swiper-navigation-color": "#FFC001", width: "100%" }}
            loop={media.length > 5}
            navigation={true}
            spaceBetween={8}
            modules={[Navigation, Scrollbar]}
            breakpoints={{
              320: { slidesPerView: Math.min(media.length, 4) },
              600: { slidesPerView: Math.min(media.length, 5) },
              900: { slidesPerView: Math.min(media.length, 6) },
            }}
          >
            {media.map((image, index) => (
              <SwiperSlide key={image.url}>
                <Box
                  onClick={() => setSelectedMedia(image.url)}
                  sx={{
                    aspectRatio: "1",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: selectedMedia === image.url ? "2px solid #FFC001" : "2px solid #222",
                    boxShadow: selectedMedia === image.url ? "0 0 10px rgba(255,192,1,0.35)" : "none",
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                    backgroundColor: "#0d0d0d",
                    transform: selectedMedia === image.url ? "scale(1.05)" : "scale(1)",
                    "&:hover": {
                      transform: "scale(1.07)",
                      borderColor: selectedMedia === image.url ? "#FFC001" : "#444",
                    },
                  }}
                >
                  {isVideo(image.url) ? (
                    <video src={image.url} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  ) : (
                    <img src={image.url} alt={`media-${index}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  )}
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </Box>
    </>
  );
};

export default ListImage;
